import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Loader2, Plus, Leaf, Target, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatCard } from "../components/shared";
import EntityDrawer from "../components/EntityDrawer";

const CATS = ["Carbon", "Waste", "Energy", "Water", "Community", "Biodiversity"];
const ESG_FIELDS = [
  { key: "category", label: "Category", type: "select", options: CATS.map((c) => ({ value: c, label: c })) },
  { key: "title", label: "Title", type: "text" },
  { key: "progress", label: "Progress (%)", type: "number" },
  { key: "status", label: "Status", type: "select", options: [{ value: "on_track", label: "On track" }, { value: "at_risk", label: "At risk" }] },
  { key: "owner", label: "Owner", type: "text" },
  { key: "target", label: "Target", type: "text" },
];

export default function ESG() {
  const [summary, setSummary] = useState(null);
  const [inits, setInits] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "Carbon", progress: 0 });
  const [sel, setSel] = useState(null);

  const load = async () => {
    const [s, i] = await Promise.all([api.get("/esg/summary"), api.get("/esg/initiatives")]);
    setSummary(s.data); setInits(i.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title) return;
    try { await api.post("/esg/initiatives", { ...form, progress: Number(form.progress) }); toast.success("Initiative added"); setOpen(false); setForm({ category: "Carbon", progress: 0 }); load(); }
    catch { toast.error("Failed to save"); }
  };

  if (!summary) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;

  return (
    <div>
      <PageHeader title="ESG & Sustainability" subtitle="Carbon, waste, energy and community initiatives with live environmental metrics.">
        <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="new-initiative-btn"><Plus className="mr-2 h-4 w-4" /> New Initiative</Button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard icon={Leaf} label="Initiatives" value={summary.initiatives_total} tone="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" />
        <StatCard icon={Target} label="On track" value={summary.on_track} />
        <StatCard icon={TrendingUp} label="Avg progress" value={`${summary.avg_progress}%`} tone="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="rounded-2xl border-border p-6 lg:col-span-2">
          <div className="mb-4 text-sm font-semibold">Environmental Metrics (6 months)</div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={summary.metrics} margin={{ left: -20 }}>
              <defs>
                <linearGradient id="c1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                <linearGradient id="c2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} /><stop offset="100%" stopColor="#f59e0b" stopOpacity={0} /></linearGradient>
                <linearGradient id="c3" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4338ca" stopOpacity={0.3} /><stop offset="100%" stopColor="#4338ca" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="carbon" stroke="#10b981" strokeWidth={2.5} fill="url(#c1)" name="Carbon (t)" />
              <Area type="monotone" dataKey="energy" stroke="#4338ca" strokeWidth={2.5} fill="url(#c3)" name="Energy (MWh)" />
              <Area type="monotone" dataKey="waste" stroke="#f59e0b" strokeWidth={2.5} fill="url(#c2)" name="Waste (t)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="rounded-2xl border-border p-6">
          <div className="mb-4 text-sm font-semibold">Progress by Category</div>
          <div className="space-y-4">
            {summary.categories.map((c) => (
              <div key={c.name}>
                <div className="mb-1 flex justify-between text-xs"><span className="text-muted-foreground">{c.name}</span><span className="font-semibold">{c.progress}%</span></div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${c.progress}%` }} /></div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {inits.map((i) => (
          <Card key={i.id} onClick={() => setSel(i)} data-testid={`esg-tile-${i.id}`}
            className="cursor-pointer rounded-2xl border-border p-5 transition-all hover:-translate-y-1 hover:shadow-md">
            <div className="flex items-center justify-between">
              <Badge variant="secondary" className="rounded-full">{i.category}</Badge>
              <Badge className={`rounded-full ${i.status === "on_track" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-amber-500/10 text-amber-600 dark:text-amber-400"}`}>{i.status === "on_track" ? "On track" : "At risk"}</Badge>
            </div>
            <div className="mt-3 text-sm font-medium">{i.title}</div>
            <div className="mt-3 flex items-center justify-between text-xs"><span className="text-muted-foreground">Progress</span><span className="font-semibold">{i.progress}%</span></div>
            <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${i.progress}%` }} /></div>
            {i.notes?.length > 0 && <div className="mt-3 text-xs text-muted-foreground">{i.notes.length} note{i.notes.length > 1 ? "s" : ""}</div>}
          </Card>
        ))}
      </div>

      {sel && (
        <EntityDrawer open={!!sel} onClose={() => setSel(null)} title={sel.title} collection="esg_initiatives"
          patchBase="/esg/initiatives" fields={ESG_FIELDS} item={sel} onSaved={() => load()} />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">New ESG Initiative</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="esg-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="esg-title" /></div>
            <div className="space-y-1.5"><Label>Progress (%)</Label><Input type="number" min="0" max="100" value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })} data-testid="esg-progress" /></div>
            <div className="space-y-1.5"><Label>Owner</Label><Input value={form.owner || ""} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="esg-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
